import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { PrismInput } from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import { modifier } from 'ember-modifier';
import { isGoogleMapsAvailable } from 'lumen/utils/google-maps';

interface GeneralFormFieldsLocationInputSignature {
  Args: {
    locationError: { hasError: boolean; message: string } | null;
    setLocation: (
      location: string,
      place?: google.maps.places.PlaceResult,
    ) => void;
  };
}

export default class GeneralFormFieldsLocationInput extends Component<GeneralFormFieldsLocationInputSignature> {
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];

  @tracked location: string = '';

  initializeSearch = modifier((element): void => {
    this.locationAutoComplete(element as HTMLInputElement);
  });

  locationAutoComplete(input: HTMLInputElement): void {
    if (!isGoogleMapsAvailable()) {
      return;
    }
    let autocomplete: google.maps.places.Autocomplete;
    if (
      this.hotelAndMarketService.hotel?.hotelinfo?.latitude &&
      this.hotelAndMarketService.hotel?.hotelinfo?.longitude
    ) {
      const center = new google.maps.LatLng(
        this.hotelAndMarketService.hotel.hotelinfo.latitude,
        this.hotelAndMarketService.hotel.hotelinfo.longitude,
      );
      const radius = 50000;
      const circle = new google.maps.Circle({
        center,
        radius,
      });
      const bounds = circle.getBounds();
      const options = {
        bounds: bounds || undefined,
      };
      autocomplete = new google.maps.places.Autocomplete(input, options);
    } else {
      autocomplete = new google.maps.places.Autocomplete(input);
    }

    // Specify just the place data fields that you need.
    // https://developers.google.com/places/web-service/place-data-fields
    autocomplete.setFields(['formatted_address', 'place_id']);
    autocomplete.addListener('place_changed', () => {
      const place = autocomplete.getPlace();
      if (place && place.place_id) {
        this.location = input.value; // Get the location as entered in the input by the google maps autocomplete
        this.args.setLocation(this.location, place);
      } else {
        this.args.setLocation(this.location);
      }
    });
  }

  @action sendResult(): void {
    this.args.setLocation(this.location);
  }

  @action updateLocation(event: Event): void {
    this.location = (event.target as HTMLInputElement).value;
  }

  <template>
    <PrismInput
      @errorMessage={{if @locationError @locationError.message}}
      @hasErrors={{if @locationError @locationError.hasError}}
      @label={{t "events.location"}}
      @onFocusOut={{this.sendResult}}
      @onInput={{this.updateLocation}}
      @placeholder=""
      @required={{true}}
      @requiredErrorMessage={{t "general.required"}}
      @value={{this.location}}
      id="location-input"
      {{! Setting an empty placeholder prevents Google Autocomplete to set a default https://stackoverflow.com/questions/41621886/remove-placeholder-text-from-google-maps-places-api }}
      {{this.initializeSearch}}
    />
  </template>
}
